import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@material-ui/core";
import { EpocusSeedKind, EpocusSeedSource } from "elephant-web/gql";
import { useState } from "react";
import { EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import {
  EPOCUS_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { SegmentedPeriodSelector } from "../../_lib/segmented-period-selector";
import {
  type ClearDemoStudiesResult,
  libraryHasKind,
  type SeedableStudy,
  type SeedDemoStudiesResult,
  type SeedLibrary,
  studiesOfKind,
} from "./use-seed-demo-studies";

const SEED_COUNTS = [1, 3, 5, 10] as const;

const KIND_OPTIONS: ReadonlyArray<{ key: EpocusSeedKind; label: string }> = [
  { key: EpocusSeedKind.Stills, label: "Stills" },
  { key: EpocusSeedKind.Cines, label: "Cines" },
  { key: EpocusSeedKind.Both, label: "Both" },
];

const EMPTY_POOL_MESSAGE = "This program has no imaged studies to copy yet.";

const NO_KIND_MESSAGE: Record<EpocusSeedKind, string> = {
  [EpocusSeedKind.Stills]:
    "This program has no still-only studies to copy yet.",
  [EpocusSeedKind.Cines]: "This program has no studies with cines to copy yet.",
  [EpocusSeedKind.Both]: EMPTY_POOL_MESSAGE,
};

const NO_LIBRARY_KIND_MESSAGE: Record<EpocusSeedKind, string> = {
  [EpocusSeedKind.Stills]:
    "The seed library has no stills for the chosen exam types.",
  [EpocusSeedKind.Cines]:
    "The seed library has no cines for the chosen exam types.",
  [EpocusSeedKind.Both]:
    "The seed library has nothing for the chosen exam types.",
};

const ControlRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
});

const ChipRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const examChipStyle = (selected: boolean) => ({
  height: 30,
  fontSize: rem(12),
  borderColor: EPOCUS_BORDER,
  backgroundColor: selected ? EPOCUS_SELECTED_BG : "transparent",
  color: EPOCUS_TEXT_PRIMARY,
});

const Credits = styled("details")({
  fontSize: rem(11),
  color: EPOCUS_TEXT_SECONDARY,
  "& summary": { cursor: "pointer" },
  "& ul": { margin: "4px 0 0", paddingLeft: 16 },
});

const HelperText: FCwC = ({ children }) => (
  <Typography style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}>
    {children}
  </Typography>
);

type ActionState =
  | { status: "idle" }
  | { status: "seeding" }
  | { status: "clearing" }
  | { status: "seeded"; result: SeedDemoStudiesResult }
  | { status: "cleared"; result: ClearDemoStudiesResult }
  | { status: "failed"; action: "seed" | "clear" };

const seededMessage = (result: SeedDemoStudiesResult): string => {
  switch (result.outcome) {
    case "seeded": {
      const studies = `${result.count} ${result.count === 1 ? "study" : "studies"}`;
      const exams = result.examTypes
        .map((type) => EXAM_LABELS[type])
        .join(", ");
      return [`Seeded ${studies}`, `${result.withCines} with cines`, exams]
        .filter(Boolean)
        .join(" · ");
    }
    case "empty":
      return EMPTY_POOL_MESSAGE;
    case "unavailable":
      return "No program is active for you yet. Please try again.";
  }
};

const statusMessage = (state: ActionState): string | null => {
  switch (state.status) {
    case "seeded":
      return seededMessage(state.result);
    case "cleared":
      return state.result.outcome === "cleared"
        ? `Cleared ${state.result.count} unclaimed seeded ${
            state.result.count === 1 ? "study" : "studies"
          }`
        : "No program is active for you yet. Please try again.";
    case "failed":
      return state.action === "seed"
        ? "Couldn't add the studies. Please try again."
        : "Couldn't clear the seeded studies. Please try again.";
    default:
      return null;
  }
};

export type SeedDemoStudiesControlsProps = {
  pool: readonly SeedableStudy[];
  library: SeedLibrary | null;
  loading: boolean;
  enabledExamTypes: readonly ExamType[];
  blockedReason: string | null;
  canClear: boolean;
  onSeed: (request: {
    count: number;
    kind: EpocusSeedKind;
    source: EpocusSeedSource;
    examTypes: readonly ExamType[];
  }) => Promise<SeedDemoStudiesResult>;
  onClear: () => Promise<ClearDemoStudiesResult>;
};

export const SeedDemoStudiesControls: FC<SeedDemoStudiesControlsProps> = ({
  pool,
  library,
  loading,
  enabledExamTypes,
  blockedReason,
  canClear,
  onSeed,
  onClear,
}) => {
  const [count, setCount] = useState<number>(3);
  const [kind, setKind] = useState<EpocusSeedKind>(EpocusSeedKind.Both);
  const [deselected, setDeselected] = useState<readonly ExamType[]>([]);
  const [state, setState] = useState<ActionState>({ status: "idle" });

  const resolvedSource = library
    ? EpocusSeedSource.Library
    : EpocusSeedSource.Program;
  const fromLibrary = library !== null;
  const examTypes = enabledExamTypes.filter(
    (examType) => !deselected.includes(examType),
  );
  const examTypeAvailable = (examType: ExamType) =>
    libraryHasKind(library?.byExamType[examType], kind);
  const requestable = examTypes.filter(examTypeAvailable);

  const kindReason = loading
    ? null
    : fromLibrary
      ? requestable.length > 0
        ? null
        : NO_LIBRARY_KIND_MESSAGE[kind]
      : studiesOfKind(pool, kind).length > 0
        ? null
        : NO_KIND_MESSAGE[kind];
  const reason = blockedReason ?? kindReason;
  const busy = state.status === "seeding" || state.status === "clearing";

  const toggleExamType = (examType: ExamType) =>
    setDeselected((current) =>
      current.includes(examType)
        ? current.filter((type) => type !== examType)
        : [...current, examType],
    );

  const seed = async () => {
    if (reason) return;
    setState({ status: "seeding" });
    try {
      const result = await onSeed({
        count,
        kind,
        source: resolvedSource,
        examTypes: fromLibrary ? requestable : [],
      });
      setState({ status: "seeded", result });
    } catch (error) {
      // Raw messages/stacks may carry PHI/PII — log only the error name.
      console.error("Failed to seed demo studies", {
        errorName: error instanceof Error ? error.name : typeof error,
      });
      setState({ status: "failed", action: "seed" });
    }
  };

  const clear = async () => {
    setState({ status: "clearing" });
    try {
      setState({ status: "cleared", result: await onClear() });
    } catch (error) {
      console.error("Failed to clear seeded demo studies", {
        errorName: error instanceof Error ? error.name : typeof error,
      });
      setState({ status: "failed", action: "clear" });
    }
  };

  const message = statusMessage(state);

  return (
    <>
      {fromLibrary && (
        <Box>
          <HelperText>Exam types to seed</HelperText>
          <ChipRow role="group" aria-label="Exam types to seed">
            {enabledExamTypes.map((examType) => {
              const selected = examTypes.includes(examType);
              const available = examTypeAvailable(examType);
              return (
                <Chip
                  key={examType}
                  variant="outlined"
                  clickable
                  style={examChipStyle(selected && available)}
                  disabled={!available}
                  label={EXAM_LABELS[examType]}
                  aria-pressed={selected && available}
                  onClick={() => toggleExamType(examType)}
                />
              );
            })}
          </ChipRow>
        </Box>
      )}

      <ControlRow>
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}>
          Number of studies
        </Typography>
        <Select
          variant="outlined"
          margin="dense"
          value={count}
          onChange={(event) => setCount(Number(event.target.value))}
          inputProps={{ "aria-label": "Number of studies to seed" }}
          style={{ minWidth: 72, backgroundColor: EPOCUS_WHITE }}
        >
          {SEED_COUNTS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </ControlRow>

      <SegmentedPeriodSelector
        options={KIND_OPTIONS}
        value={kind}
        onChange={(next: EpocusSeedKind) => setKind(next)}
        label="Kind of study to seed"
      />

      <ControlRow>
        <Button
          variant="outlined"
          size="small"
          onClick={seed}
          disabled={busy || Boolean(reason)}
          style={{
            color: EPOCUS_TEXT_PRIMARY,
            borderColor: EPOCUS_BORDER,
            backgroundColor: EPOCUS_WHITE,
          }}
          startIcon={
            state.status === "seeding" ? (
              <CircularProgress size={14} style={{ color: "inherit" }} />
            ) : undefined
          }
        >
          Seed demo studies
        </Button>
        {canClear && (
          <Button
            variant="text"
            size="small"
            onClick={clear}
            disabled={busy}
            style={{ color: EPOCUS_TEXT_SECONDARY }}
            startIcon={
              state.status === "clearing" ? (
                <CircularProgress size={14} style={{ color: "inherit" }} />
              ) : undefined
            }
          >
            Clear unclaimed seeded studies
          </Button>
        )}
      </ControlRow>
      {reason && <HelperText>{reason}</HelperText>}
      {message && (
        <Typography
          role="status"
          style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
        >
          {message}
        </Typography>
      )}

      {library && library.attribution.length > 0 && (
        <Credits>
          <summary>Image credits</summary>
          <ul>
            {library.attribution.map((source) => (
              <li key={source.dataset}>
                {source.title} ({source.licence})
                {source.url ? (
                  <>
                    {" "}
                    <a href={source.url} target="_blank" rel="noreferrer">
                      source
                    </a>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </Credits>
      )}
    </>
  );
};
